import type { ReactNode } from 'react';
import { cn } from './cn';

export interface CardProps {
  /** Optional header title (left side). */
  title?: ReactNode;
  /** Optional header extra (right side — hint text, action, link). */
  extra?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Override the body padding/!layout. */
  bodyClassName?: string;
  /** Body padding density: 'default' = p-4 (16px); 'compact' = p-3 (12px) for nested/inner cards.
   *  Use this instead of a bodyClassName p-3! override. */
  density?: 'default' | 'compact';
  /** Lift + shadow on hover; pair with onClick for clickable cards. */
  hoverable?: boolean;
  onClick?: () => void;
  style?: import('react').CSSProperties;
  /** antd `loading` — shows a skeleton body until content is ready. */
  loading?: boolean;
}

/** Surface container with an optional header (replaces antd Card). */
export function Card({ title, extra, children, className, bodyClassName, density = 'default', hoverable, onClick, style, loading }: CardProps) {
  const hasHeader = title != null || extra != null;
  return (
    <div
      style={style}
      onClick={onClick}
      className={cn(
        // `min-w-0`: a Card is nearly always a grid/flex item, and those default to `min-width: auto`
        // — so a card refuses to shrink below its widest content and drags the whole page sideways
        // instead (a long "AGN 500ml — AGNWH-BANGLORE" row made /dashboard/inventory 93px too wide on
        // a phone). Letting the card shrink lets its own content wrap/truncate, which is what the
        // truncate + wrap rules inside it are already written for.
        // A card is LIFTED off the canvas — the hairline draws its edge, `shadow-e1` (the resting
        // step of the ladder in theme.css) puts light around it. The shadow is deliberately the
        // gentlest step there is: a card is not a dialog, and a page of twelve cards each throwing
        // a dialog-sized shadow is the "floating slab" look. The border stays because dark mode has
        // no drop shadow to speak of — there, the hairline and the inset highlight do the whole job.
        'min-w-0 rounded-card border border-line bg-surface shadow-e1',
        // Hover RAISES (and warms the hairline); it must never move the card — see the same rule on
        // ui/StatCard. `hover:` carries pseudo-class specificity, so it beats the resting shadow.
        hoverable && 'transition-shadow duration-150 hover:border-brand-300 hover:shadow-e2',
        (onClick || hoverable) && 'cursor-pointer',
        className,
      )}
    >
      {hasHeader && (
        // `flex-wrap` + `gap-y-1`: `extra` is `shrink-0` (so a button or tag in it is never squashed),
        // which means it contributes its FULL width to the header's min-content. On a narrow column a
        // wordy hint ("These fields are yours to change", 188px) therefore forced the whole card — and
        // the grid column holding it — wider than the phone. Wrapping lets `extra` drop to its own line
        // instead of widening the card; there is nothing to wrap once the row fits.
        // The header carries a whisper of brand green (6%) fading to nothing by the rule below it,
        // so a card reads as a titled surface rather than a white box with a line across it. The
        // radius is the card's own MINUS the 1px border, so the tint follows the rounded corner
        // exactly instead of squaring it off (the card cannot use `overflow-hidden` — that would
        // make it a scroll container and break any `position: sticky` used inside one).
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-t-[calc(var(--radius-card)_-_1px)] border-b border-line bg-linear-to-b from-brand-500/[0.06] to-transparent px-4 py-3">
          {/* 14px semibold, not the page-level section title. A card heading sits BELOW the
              page heading in the hierarchy; at `type-section-title` it competed with it and
              every card shouted as loudly as the screen it was on. */}
          <div className="min-w-0 overflow-hidden break-words text-sm font-semibold text-content">{title}</div>
          {/*
            * `shrink-0` here defeated the `flex-wrap` above that exists to handle exactly this case.
            * A long caption — "4 of 4 warehouses have work · click a row to open its dispatches" —
            * did drop to its own line and then kept its 339px max-content width on a 316px card, so
            * the whole PAGE scrolled sideways at 360px (measured on /warehouse/dashboard; the table
            * beside it was innocent, sitting correctly inside its own overflow-x-auto box).
            *
            * Allowing it to shrink does not squash a row of action buttons: flex decides WRAPPING
            * from each item's base size, so a control row still drops to its own line first, and
            * only then uses the width it has.
            */}
          {extra != null && <div className="min-w-0 max-w-full break-words text-xs text-muted">{extra}</div>}
        </div>
      )}
      <div className={cn(density === 'compact' ? 'p-3' : 'p-4', bodyClassName)}>
        {loading ? (
          <div className="space-y-2.5" aria-busy="true">
            {[92, 78, 85, 60].map((w, i) => (
              <div key={i} className="h-3.5 animate-pulse rounded bg-surface-3" style={{ width: `${w}%` }} />
            ))}
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}
